import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { RouterService } from './chats/shared/services/router.service';

/**
 * App-wide "you no longer have access" notice. When a resource a user is
 * actively viewing — a teammate, or the skill behind the open conversation — is
 * revoked live and drops out of the store, the page sends them to the landing
 * page and raises a toast via the toastr service (`ngx-toastr`). Toastr renders,
 * positions, and auto-dismisses the toast from its own global overlay, so it
 * survives the redirect without an app-root host.
 */
@Injectable({ providedIn: 'root' })
export class AccessRevokedNoticeService {
  private readonly routerService = inject(RouterService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  /** The notice in flight, to drop a duplicate fired during the redirect. */
  private activeMessageKey: string | null = null;

  /** Redirect to the landing page and surface the access-removed toast. */
  public async notify(messageKey: string): Promise<void> {
    // Idempotent: ignore a repeat of the active notice — e.g. a page effect
    // re-firing during a state-change burst before the redirect completes.
    if (this.activeMessageKey === messageKey) {
      return;
    }
    this.activeMessageKey = messageKey;
    this.toastrService.error(
      this.translateService.instant(messageKey) as string,
      undefined,
      { closeButton: true },
    );
    try {
      await this.routerService.navigateToLanding();
    } finally {
      this.activeMessageKey = null;
    }
  }
}
